import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "@/i18n"
import { useProfileEvent } from "@/hooks/useProfiles"
import { usePublish } from "@/hooks/usePublish"
import { buildProfileDraft } from "@/nostr/api/social"
import { Modal } from "./Modal"
import { Avatar } from "./Avatar"

type Field =
  | "name"
  | "display_name"
  | "about"
  | "picture"
  | "banner"
  | "nip05"
  | "lud16"
  | "website"

const EMPTY: Record<Field, string> = {
  name: "",
  display_name: "",
  about: "",
  picture: "",
  banner: "",
  nip05: "",
  lud16: "",
  website: "",
}

export interface ProfileEditorDialogProps {
  open: boolean
  onClose: () => void
  pubkey: string
  onSaved?: () => void
}

/** Edits and publishes the kind 0 metadata of the signed-in user. */
export function ProfileEditorDialog({ open, onClose, pubkey, onSaved }: ProfileEditorDialogProps) {
  const { t } = useTranslation()
  const event = useProfileEvent(pubkey)
  const { publishDraft } = usePublish()
  const [fields, setFields] = useState<Record<Field, string>>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()

  const previous = useMemo<Record<string, unknown>>(() => {
    if (!event?.content) return {}
    try {
      const parsed = JSON.parse(event.content) as unknown
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {}
    } catch {
      return {}
    }
  }, [event])

  useEffect(() => {
    if (!open) return
    const read = (key: Field): string => {
      const value = previous[key]
      return typeof value === "string" ? value : ""
    }
    setFields({
      name: read("name"),
      display_name: read("display_name"),
      about: read("about"),
      picture: read("picture"),
      banner: read("banner"),
      nip05: read("nip05"),
      lud16: read("lud16"),
      website: read("website"),
    })
    setError(undefined)
  }, [open, previous])

  const setField = (key: Field, value: string) => setFields((current) => ({ ...current, [key]: value }))

  const save = async () => {
    setSaving(true)
    setError(undefined)
    const outcome = await publishDraft(buildProfileDraft(fields, previous, pubkey), {
      successMessage: "Profile saved",
    })
    setSaving(false)
    if (outcome.ok) {
      onSaved?.()
      onClose()
    } else {
      setError(outcome.error ?? t("Could not publish the profile"))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Edit profile")}
      description={t("Published as a kind 0 metadata event to your relays.")}
      size="medium"
    >
      <div className="profile-editor">
        <div className="profile-editor-preview">
          <Avatar src={fields.picture || undefined} label={fields.display_name || fields.name || pubkey} size={48} />
          <div>
            <p className="profile-editor-name">{fields.display_name || fields.name || t("No metadata")}</p>
            <p className="muted small">{fields.nip05 || `${pubkey.slice(0, 16)}…`}</p>
          </div>
        </div>

        <form
          className="profile-editor-form"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault()
            void save()
          }}
        >
          <label>
            <span>{t("Display name")}</span>
            <input value={fields.display_name} onChange={(event) => setField("display_name", event.target.value)} maxLength={80} />
          </label>
          <label>
            <span>{t("Name")}</span>
            <input value={fields.name} onChange={(event) => setField("name", event.target.value)} maxLength={80} />
          </label>
          <label className="profile-editor-wide">
            <span>{t("About")}</span>
            <textarea value={fields.about} onChange={(event) => setField("about", event.target.value)} rows={3} maxLength={2000} />
          </label>
          <label>
            <span>{t("Avatar URL")}</span>
            <input value={fields.picture} onChange={(event) => setField("picture", event.target.value)} placeholder="https://…" />
          </label>
          <label>
            <span>{t("Banner URL")}</span>
            <input value={fields.banner} onChange={(event) => setField("banner", event.target.value)} placeholder="https://…" />
          </label>
          <label>
            <span>NIP-05</span>
            <input value={fields.nip05} onChange={(event) => setField("nip05", event.target.value)} placeholder="you@example.com" />
          </label>
          <label>
            <span>{t("Lightning address")}</span>
            <input value={fields.lud16} onChange={(event) => setField("lud16", event.target.value)} placeholder="you@getalby.com" />
          </label>
          <label className="profile-editor-wide">
            <span>{t("Website")}</span>
            <input value={fields.website} onChange={(event) => setField("website", event.target.value)} placeholder="https://…" />
          </label>

          {error && (
            <p className="form-error profile-editor-wide" role="alert">
              {error}
            </p>
          )}

          <div className="profile-editor-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {t("Cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t("Publishing") : t("Save")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
